import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import useAnalyticsStore from 'components/sections/analytics/store/use-analytics-store';
import { WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { StereoVideoType } from 'lib_ts/enums/videos.enums';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import { IPitchRecapUpdate } from 'lib_ts/interfaces/pitches/i-common-pitch-recap';
import { useEffect } from 'react';

// load the recaps for the current session x hitter on mount, and then listen for updates as they arrive
const useSyncRecapLive = (props: {
  machineID: Nullable<string>;
  sessionID: Nullable<string>;
  hitterID: Nullable<string>;
}) => {
  const hits = useAnalyticsStore((s) => s.hits);

  // MARK: on mount, do a one-time fetch of all hits for the current session x hitter
  useEffect(() => {
    useAnalyticsStore.setState({
      newestHitID: null,
      updatedHitID: null,
    });

    if (!props.sessionID) {
      return;
    }

    if (!props.machineID) {
      return;
    }

    useAnalyticsStore.getState().fetchHitsAndMachine({
      machineID: props.machineID,
      sessionID: props.sessionID,
      hitterID: props.hitterID, // optional, e.g. no hitter selected
    });

    return () => {
      // reset hits to empty array
      useAnalyticsStore.setState({ hits: [] });
    };
  }, [props.hitterID, props.sessionID, props.machineID]);

  // MARK: listen for pitch recap updates and update the hits list
  useEffect(() => {
    const handleUpdate = async (event: CustomEvent) => {
      const data = event.detail as IPitchRecapUpdate;
      const { hitID, sessionID, hitterID } = data;

      useAnalyticsStore.setState({
        newestHitID: null,
        updatedHitID: null,
      });

      if (!hitID) {
        return;
      }

      if (props.sessionID !== sessionID) {
        // not for current session
        return;
      }

      // recap is for a different hitter than we're looking at
      if (props.hitterID !== hitterID) {
        return;
      }

      const existingHit = hits.find((h) => h._id === hitID);

      if (!existingHit || !data.video) {
        // store will handle adding to hits list
        await useAnalyticsStore.getState().fetchHit(hitID);
        useAnalyticsStore.setState({
          newestHitID: hitID,
          updatedHitID: null,
        });
        return;
      }

      // As of June 2026, we only show annotated videos to users
      if (data.video.type !== StereoVideoType.Annotated) {
        return;
      }

      await useAnalyticsStore
        .getState()
        .addAnnotatedVideo(hitID, data.video._id);

      useAnalyticsStore.setState({
        newestHitID: null,
        updatedHitID: hitID,
      });
    };

    WebSocketHelper.on(WsMsgType.S2U_PitchRecapUpdate, handleUpdate);
    return () =>
      WebSocketHelper.remove(WsMsgType.S2U_PitchRecapUpdate, handleUpdate);
  }, [props.hitterID, props.sessionID, hits]);
};

export default useSyncRecapLive;
